import { Autocomplete, TextField } from "@mui/material";
import type { GridRenderEditCellParams, GridValidRowModel } from "@mui/x-data-grid-premium";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { useLayoutEffect, useRef } from "react";

interface Option {
  value: string;
  label: string;
}

interface Props extends GridRenderEditCellParams<GridValidRowModel, string> {
  valueOptions: Option[];
  openOnFocus?: boolean;
  commitChangesOnSelect?: boolean;
  loading?: boolean;
  noOptionsText?: string;
  onOpen?: () => void;
}

const GridEditAutocompleteCell = ({
  valueOptions,
  openOnFocus,
  commitChangesOnSelect,
  loading,
  noOptionsText,
  onOpen,
  id,
  field,
  value,
  hasFocus,
}: Props) => {
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (hasFocus) {
      inputRef.current?.focus();
    }
  }, [hasFocus]);

  const handleValueChange = (_: React.SyntheticEvent, newValue: Option | null) => {
    apiRef.current.setEditCellValue({ id, field, value: newValue?.value ?? "" });
    if (commitChangesOnSelect) {
      apiRef.current.stopCellEditMode({ id, field });
    }
  };

  const selectedOption = valueOptions.find((option) => option.value === value);

  return (
    <Autocomplete
      fullWidth
      options={valueOptions}
      value={selectedOption ?? null}
      onChange={handleValueChange}
      getOptionLabel={({ label }) => label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      openOnFocus={openOnFocus}
      loading={loading}
      noOptionsText={noOptionsText}
      onOpen={onOpen}
      slotProps={{
        popper: {
          style: { minWidth: "max-content" },
        },
      }}
      renderInput={(params) => <TextField inputRef={inputRef} {...params} autoFocus />}
      sx={{
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
      }}
    />
  );
};

export default GridEditAutocompleteCell;
